import { i18n } from "../i18n";
import "../i18n";
import { MODAL_STEPS } from "../constants/modal";
import { AVAILABLE_LANGUAGES } from "../types/domains";
import { findBrandByDomain } from "../configs/configLoader";
import { inputDesktopWidthOffset } from "../configs";
import { isFootwear } from "./categoryParams";

export const initializeLanguage = () => {
  const defaultLanguage = "fr";
  const url = new URL(window.location.href);
  const params = new URLSearchParams(window.location.search);

  const lang = params.get("lang")?.split("-")[0].toLowerCase();

  const langRes =
    (lang && AVAILABLE_LANGUAGES.includes(lang) && lang) || defaultLanguage;

  if (!params.has("lang")) {
    params.append("lang", langRes);
    url.search = params.toString();
    window.history.replaceState({}, "", url.toString());
  }

  i18n.changeLanguage(langRes);

  return langRes;
};

export const convertHeightToFeetAndInches = (heightInCm: string) => {
  const heightStr = heightInCm.toString();

  const paddedHeightStr = heightStr.padStart(2, "0");

  const feetValue = paddedHeightStr[0];
  const inchesValue = paddedHeightStr[1];

  return { feetValue, inchesValue };
};

export const convertFeetAndInchesToCm = (
  feetValue: string,
  inchesValue: string,
) => {
  const heightStr = `${feetValue}${inchesValue}`;

  return heightStr;
};

export const INCHtoCM = (v: any) => {
  const [feet, inches] = v.split(" ").map(Number);
  return (feet * 30.48 + inches * 2.54).toFixed(2).toString();
};

export const LBStoKG = (v: number) => (v * 0.453592).toFixed(2).toString();

export const getStepMaps = (isMobile: boolean) => {
  const stepLabels = ["ABOUT YOU", "HIPS", "STOMACH", "CHEST", "YOUR SIZE"];

  const labelToKeyMap: Record<string, keyof typeof MODAL_STEPS> = {
    "ABOUT YOU": isMobile ? "INTRO_MOBILE" : "INTRO",
    HIPS: "BELLY",
    STOMACH: "TORSO",
    CHEST: "BREASTS",
    "YOUR SIZE": "RESULT",
  };

  const stepMap = stepLabels.map((label) => {
    const key = labelToKeyMap[label];
    const stepNumber = MODAL_STEPS[key].number;

    return { label, stepNumber };
  });

  return stepMap;
};

export const getInputStyles = ({
  id,
  isMobile,
  isAndroid,
  font,
  inputStyles,
  inputStyleSelector,
}: {
  id: string;
  isMobile: boolean;
  isAndroid: boolean;
  font: string;
  inputStyles: React.CSSProperties;
  inputStyleSelector: { borderBottom?: string; border?: string };
}): React.CSSProperties => {
  const brandDefined = findBrandByDomain();

  const isSRP = brandDefined?.name === "SRP";
  const isVB = brandDefined?.name === "Victoria Beckham";
  const isOtherBrand = !isSRP && !isVB;
  const isBlackstore = brandDefined?.name === "Blackstore";
  const isGivenchy = brandDefined?.name === "Givenchy";

  const customStyles = isGivenchy || isVB;

  const borderLeftWidth =
    customStyles && id === "inches" ? "0px" : inputStyles.borderWidth;

  const isFeetOrInches = id === "feet" || id === "inches";

  const calcWidthMobile = () =>
    customStyles ? "calc(100% - 32px)" : "calc(100% - 42px)";
  const calcWidthDesktop = () => `calc(100% - ${inputDesktopWidthOffset})`;

  const getWidth = () => {
    if (!isMobile && isFeetOrInches && isBlackstore) return "100%";
    return isMobile ? calcWidthMobile() : calcWidthDesktop();
  };

  const getMaxWidth = () => {
    if (isBlackstore && !isMobile) return "calc(100% - 20px)";

    if (isMobile && (isVB || isOtherBrand)) {
      return calcWidthMobile();
    }

    if (inputStyleSelector.borderBottom === "none" || isSRP || isVB) {
      return "calc(100% - 20px)";
    }

    return "100%";
  };

  const fontFamily =
    !isAndroid && font !== "Futura" ? `${font}, sans-serif !important` : "";

  return {
    ...inputStyles,
    borderStyle: inputStyles.borderStyle,
    borderWidth: inputStyles.borderWidth,
    borderLeftWidth,
    width: getWidth(),
    maxWidth: getMaxWidth(),
    position: "relative",
    outline: "none",
    fontFamily,
    ...(isFeetOrInches && !isMobile ? { flex: 1, minWidth: 0 } : {}),
  };
};

export const getSubdomainPrefixUpper = (domain: string): string => {
  const parts = domain.split(".");
  if (parts.length > 2) {
    return parts[0].toUpperCase();
  }
  return "";
};

export const getLangBase = (lang: string): string =>
  lang?.split(/[-/]/)[1]?.toLowerCase() ||
  lang?.split(/[-/]/)[0]?.toLowerCase() ||
  "default";

const brandsWithArrow = ["Blackstore"];

export const isWithArrow = (brandName: string | undefined) => {
  return brandName && brandsWithArrow.includes(brandName);
};

export const getCloseButtonSize = (
  isMobile: boolean,
  brandName: string | undefined,
  standartSize: { mobile: string; desktop: string },
  customSize: { mobile: string; desktop: string },
): string => {
  const isBlackstore = brandName === "Blackstore";
  const isIntersport = brandName === "Intersport";
  const isTDF = brandName === "TDF";

  const isCustomRetailer = isBlackstore || isIntersport || isTDF;

  return isCustomRetailer
    ? isMobile
      ? customSize.mobile
      : customSize.desktop
    : isMobile
      ? standartSize.mobile
      : standartSize.desktop;
};

export const getErrorTitle = ({ outOfRange }: { outOfRange: boolean }) => {
  const brandDefined = findBrandByDomain();
  const isIntersport = brandDefined?.name === "Intersport";
  const isBlackstore = brandDefined?.name === "Blackstore";
  const isCustomInterface = isIntersport || isBlackstore;

  if (isCustomInterface) {
    if (outOfRange && isFootwear) return "steps.error_shoe_outOfRange";
    if (isFootwear && !outOfRange) return "steps.error_shoe";
    if (!isFootwear && outOfRange) return "steps.error_outOfRange";

    return "steps.error";
  }

  return "steps.error";
};
